import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import GrowthMetricsSelect from "./GrowthMetricsSelect";
import { useBrainstormingIdeaDialog } from "@/contexts/BrainstormingIdeaDialogContext";
import { useGrowthSquad } from "@/hooks/useGrowthSquad";
import { useSalesChannels } from "@/hooks/useSalesChannels";
import { useBrainstormingIdeas } from "@/hooks/useBrainstormingIdeas";
import { useBrandManager } from "@/hooks/useBrandManager";

const GlobalBrainstormingIdeaDialog = () => {
  const {
    isOpen,
    isEditing,
    editingIdea,
    formData,
    setFormData,
    closeDialog,
    onSuccessCallback
  } = useBrainstormingIdeaDialog();

  const { currentBrandId } = useBrandManager();
  const { squadMembers } = useGrowthSquad();
  const { salesChannels } = useSalesChannels();
  const { createIdea, updateIdea, isLoading } = useBrainstormingIdeas(formData.sales_channel_id);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentBrandId) {
      console.error("No current brand selected");
      return;
    }
    
    if (!isEditing) {
      if (!formData.sales_channel_id) {
        toast({
          title: "Validation Error",
          description: "Please select a sales channel",
          variant: "destructive",
        });
        return;
      }
      if (!formData.submitted_by) {
        toast({
          title: "Validation Error",
          description: "Please select who submitted this idea",
          variant: "destructive",
        });
        return;
      }
    }
    
    let success = false;
    
    if (isEditing && editingIdea) {
      const updateData = {
        name: formData.name,
        affecting_metric: [formData.affecting_metric],
        description: formData.description
      };
      success = await updateIdea(editingIdea.id, updateData);
    } else {
      const createData = {
        name: formData.name,
        affecting_metric: [formData.affecting_metric],
        description: formData.description,
        submitted_by: formData.submitted_by,
        sales_channel_id: formData.sales_channel_id,
        brand_id: currentBrandId
      };
      success = await createIdea(createData);
    }
    
    if (success) {
      onSuccessCallback?.();
      closeDialog();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeDialog();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Brainstorming Idea" : "Add Brainstorming Idea"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the brainstorming idea details."
              : "Submit a new growth idea to the brainstorming collection."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label htmlFor={isEditing ? "edit-name" : "name"}>Idea Name <span className="text-destructive">*</span></Label>
              <Input
                id={isEditing ? "edit-name" : "name"}
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            
            {!isEditing && (
              <div>
                <Label htmlFor="sales_channel">Sales Channel <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.sales_channel_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, sales_channel_id: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sales channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {(salesChannels || []).map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.sales_channel_id && (
              <GrowthMetricsSelect
                salesChannelId={formData.sales_channel_id}
                selectedMetric={formData.affecting_metric}
                onMetricChange={(metric) => setFormData(prev => ({ ...prev, affecting_metric: metric }))}
              />
            )}

            {!isEditing && (
              <div>
                <Label htmlFor="submitted_by">Submitted By <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.submitted_by}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, submitted_by: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select squad member" />
                  </SelectTrigger>
                  <SelectContent>
                    {(squadMembers || []).map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name} ({member.alias})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor={isEditing ? "edit-description" : "description"}>Description</Label>
              <Textarea
                id={isEditing ? "edit-description" : "description"}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your growth idea..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isEditing ? "Update Idea" : "Add Idea"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalBrainstormingIdeaDialog;
